import React, {ReactElement, ReactNode, useCallback, useMemo} from 'react';
import {Link} from '@material-ui/core';
import {Trans, useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import PublicPageLayout, {
  PublicPageContentLayout
} from 'src/modules/payments/PublicPageLayout/PublicPageLayout';
import HelperUtils from '@shared/utils/HelperUtils';
import PhoneFormatter from '@components/system/PhoneFormatter/PhoneFormatter';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';
import {environment} from '@shared/environment/environment';
import {State} from '@shared/store/reducers/Index';
import {VerifyAppointmentSchedulerTokenResponse} from '@shared/services/service-mappers/AppointmentSchedulingMapper';

type AppointmentPortalPublicPageLayoutProps = {
  children: ReactNode;
  className?: string;
  testId?: string;
  contentLayout?: PublicPageContentLayout;
  afterContent?: ReactNode;
  apiResponseData?: VerifyAppointmentSchedulerTokenResponse | null;
};

const AppointmentPortalPublicPageLayout = ({
  children,
  className = 'payment-login-page',
  testId = 'pcl-appointmentPortalPage',
  contentLayout = 'plain',
  afterContent,
  apiResponseData: apiResponseDataProp
}: AppointmentPortalPublicPageLayoutProps): ReactElement => {
  const {t} = useTranslation();
  const apiResponseDataFromStore = useSelector(
    (state: State) => state.appointmentPortal.details
  );
  const apiResponseData = apiResponseDataProp ?? apiResponseDataFromStore;

  const renderPortalHosTitle = useCallback(() => {
    if (apiResponseData?.clinic?.name) {
      return apiResponseData.clinic.name;
    }
    return apiResponseData?.group?.group_name;
  }, [apiResponseData]);

  const getSupportEmail = useCallback(() => {
    if (
      apiResponseData?.group?.patients_shared === true &&
      apiResponseData?.group?.contact_email
    ) {
      return apiResponseData.group.contact_email;
    }
    if (apiResponseData?.clinic?.id && apiResponseData?.clinic?.contact_email) {
      return apiResponseData.clinic.contact_email;
    }
    return environment.supportEmail;
  }, [apiResponseData]);

  const addressLine1 = useMemo(() => {
    if (apiResponseData?.clinic?.id) {
      return HelperUtils.formatFullAddress({
        line1: apiResponseData.clinic?.address,
        line2: apiResponseData.clinic?.address_line_2 || '',
        city: '',
        state: '',
        country: '',
        zip: ''
      });
    }
    return HelperUtils.formatFullAddress({
      line1: apiResponseData?.group?.address,
      line2: apiResponseData?.group?.address_line_2 || '',
      city: '',
      state: '',
      country: '',
      zip: ''
    });
  }, [apiResponseData]);

  const addressLine2 = useMemo(() => {
    if (apiResponseData?.clinic?.id) {
      return HelperUtils.formatFullAddress({
        line1: '',
        line2: '',
        city: apiResponseData?.clinic?.city,
        state: apiResponseData?.clinic?.state,
        country: '',
        zip: (apiResponseData?.clinic?.zip || '').toString()
      });
    }
    return HelperUtils.formatFullAddress({
      line1: '',
      line2: '',
      city: apiResponseData?.group?.city || '',
      state: apiResponseData?.group?.state || '',
      zip: (apiResponseData?.group?.zip || '').toString()
    });
  }, [apiResponseData]);

  const practicePhones = useMemo((): ReactNode => {
    const clinicDetails = apiResponseData?.clinic;
    if (!clinicDetails) {
      return null;
    }

    const formatPhone = (phone: string): string =>
      phone.startsWith('+1') ? phone : `+1${phone}`;

    if (clinicDetails.phone === clinicDetails.billing_phone) {
      return clinicDetails.phone ? (
        <div className="clinic-phone">
          {t('PAYMENTS_LOGIN.PHONE_QUERY_LABEL')}
          <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
        </div>
      ) : null;
    }

    return (
      <>
        {clinicDetails.phone ? (
          <div className="clinic-phone">
            {t('PAYMENTS_LOGIN.PHONE')}
            <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
          </div>
        ) : null}
        {clinicDetails.billing_phone ? (
          <div className="clinic-phone">
            {t('PAYMENTS_LOGIN.BILLING_PHONE')}
            <PhoneFormatter value={formatPhone(clinicDetails.billing_phone)} />
          </div>
        ) : null}
      </>
    );
  }, [apiResponseData, t]);

  const defaultAfterContent = (
    <div className="payment-login-after-content">
      <section className="flex-centering contact-section">
        <p className="contact-us-text">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.CONTACT_INFO"
            values={{
              supportEmail: getSupportEmail()
            }}
            components={[
              <Link href={`mailto:${getSupportEmail()}`} key="support-email" />
            ]}
          />
        </p>
      </section>

      <div className="tag-wrapper flex-centering">
        <p className="tag-text">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.APP_CLOSING_INFORMATION_PART1"
            components={[<span key="closing-information" />]}
          />
        </p>
        <p className="tag-text-2">
          {t('GUARANTOR_PORTAL.LOGIN_SCREEN.APP_CLOSING_INFORMATION_PART2')}
        </p>
        <Link href={`${environment.websiteDetails.name}`} target="pcl">
          <MedopsLogo className="logo" />
        </Link>
      </div>
    </div>
  );

  return (
    <PublicPageLayout
      className={className}
      testId={testId}
      showHero={false}
      contentLayout={contentLayout}
      practiceBadgeLabel={renderPortalHosTitle()}
      practiceName={renderPortalHosTitle()}
      addressLine1={addressLine1}
      addressLine2={addressLine2}
      practicePhones={practicePhones}
      showPracticeInfo={false}
      afterContent={afterContent ?? defaultAfterContent}>
      {children}
    </PublicPageLayout>
  );
};

export default AppointmentPortalPublicPageLayout;
